import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { staffAPI, masterSettingsAPI } from '@/api/api';
import { ArrowLeft, Save, Copy, Eye, EyeOff, X, CheckCircle, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import indianStates from '@/data/indianStatesDistricts.json';

const AddStaffPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [credentialsModal, setCredentialsModal] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [availableDistricts, setAvailableDistricts] = useState([]);
  
  const [formData, setFormData] = useState({
    // Basic Information
    first_name: '',
    middle_name: '',
    last_name: '',
    father_name: '',
    mother_name: '',
    marital_status: 'Single',
    spouse_name: '',
    date_of_birth: '',
    personal_email: '',
    phone: '',
    alternate_phone: '',
    whatsapp_no: '',
    gender: 'Male',
    blood_group: '',
    aadhaar_no: '',
    pan_no: '',
    contract_type: 'Permanent',
    address: '',
    state: '',
    district: '',
    pin_code: '',
    
    // Employment Information
    employee_id: '',
    staff_type: 'Teaching',
    department: '',
    designation: '',
    joining_date: '',
    ending_date: '',
    qualification: '',
    highest_qualification: '',
    salary: '',
    
    // Account Status
    account_status: 'Active',
    
    // Bank Details
    account_holder_name: '',
    account_type: '',
    account_number: '',
    confirm_account_number: '',
    bank_name: '',
    branch_name: '',
    ifsc_code: '',
    
    // Login Password
    password: '',
    
    // System Roles
    roles: []
  });

  // Available System Roles
  const availableRoles = [
    { key: 'Admin', name: 'Admin', description: 'Full system access' },
    { key: 'Admission Officer', name: 'Admission Officer', description: 'Admission, Seat Booking, Consultant' },
    { key: 'Accountant', name: 'Accountant', description: 'Fees, Accounts, Cash/Bank Register' },
    { key: 'HR Manager', name: 'HR Manager', description: 'Staff, Payroll, Leave, Attendance' },
    { key: 'Teacher', name: 'Teacher', description: 'Staff Portal Only' },
    { key: 'Librarian', name: 'Librarian', description: 'Library Management' },
    { key: 'Front Desk', name: 'Front Desk', description: 'Visitor, Enquiry, Complaint' },
    { key: 'Exam Controller', name: 'Exam Controller', description: 'Examination, Routine, Transcript' },
    { key: 'Inventory Manager', name: 'Inventory Manager', description: 'Inventory Management' },
    { key: 'Hostel In-charge', name: 'Hostel In-charge', description: 'Hostel Management' },
    { key: 'Transport In-charge', name: 'Transport In-charge', description: 'Transport Management' }
  ];

  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchMasterData();
    fetchNextEmployeeId();
  }, []);

  const fetchNextEmployeeId = async () => {
    try {
      const response = await staffAPI.getNextEmployeeId();
      if (response.data?.next_employee_id) {
        setFormData(prev => ({ ...prev, employee_id: response.data.next_employee_id }));
      }
    } catch (error) {
      console.error('Error fetching next employee ID:', error);
    }
  };

  const fetchMasterData = async () => {
    try {
      const [deptResponse, desigResponse] = await Promise.all([
        masterSettingsAPI.getDepartments(),
        masterSettingsAPI.getDesignations()
      ]);
      setDepartments(deptResponse.data);
      setDesignations(desigResponse.data);
    } catch (error) {
      console.error('Error fetching master data:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'department') {
      setFormData(prev => ({ ...prev, [name]: value, designation: '', staff_type: 'Teaching' }));
    } else if (name === 'designation') {
      // Auto-fill staff_type based on selected designation (value is title/name)
      const selectedDesig = designations.find(d => (d.title || d.name) === value);
      const staffType = selectedDesig?.staff_type || 'Teaching';
      setFormData(prev => ({ ...prev, designation: value, staff_type: staffType }));
    } else if (name === 'state') {
      setAvailableDistricts(indianStates[value] || []);
      setFormData(prev => ({ ...prev, [name]: value, district: '' }));
    } else {
      setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    }
  };

  const toggleRole = (roleKey) => {
    setFormData(prev => {
      const currentRoles = prev.roles || [];
      if (currentRoles.includes(roleKey)) {
        return { ...prev, roles: currentRoles.filter(r => r !== roleKey) };
      } else {
        return { ...prev, roles: [...currentRoles, roleKey] };
      }
    });
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.first_name.trim()) newErrors.first_name = 'First name is required';
    if (!formData.last_name.trim()) newErrors.last_name = 'Last name is required';
    if (!formData.date_of_birth) newErrors.date_of_birth = 'Date of birth is required';
    if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
    if (!formData.department) newErrors.department = 'Department is required';
    if (!formData.designation) newErrors.designation = 'Designation is required';
    if (!formData.joining_date) newErrors.joining_date = 'Joining date is required';
    if (!formData.state) newErrors.state = 'State is required';
    if (!formData.district) newErrors.district = 'District is required';
    if (!formData.pin_code.trim()) {
      newErrors.pin_code = 'PIN code is required';
    } else if (!/^\d{6}$/.test(formData.pin_code)) {
      newErrors.pin_code = 'PIN code must be 6 digits';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    setLoading(true);
    try {
      const staffData = {
        ...formData,
        full_name: `${formData.first_name} ${formData.middle_name} ${formData.last_name}`.trim().replace(/\s+/g, ' '),
        salary: formData.salary ? parseFloat(formData.salary) : 0
      };
      
      if (formData.personal_email && formData.personal_email.trim()) {
        staffData.email = formData.personal_email.trim();
      }
      
      const response = await staffAPI.createStaff(staffData);
      
      if (response.data.credentials) {
        setCredentialsModal({
          staffName: response.data.staff?.full_name || staffData.full_name,
          employeeId: response.data.staff?.employee_id || formData.employee_id,
          whatsapp_no: response.data.staff?.whatsapp_no || formData.whatsapp_no || formData.phone,
          ...response.data.credentials
        });
        toast.success('Staff member added successfully!');
      } else {
        toast.success('Staff member added successfully!');
        navigate('/hr/staff-list');
      }
    } catch (error) {
      console.error('Error adding staff:', error);
      let errorMessage = 'Failed to add staff member';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') errorMessage = detail;
        else if (Array.isArray(detail)) errorMessage = detail.map(err => err.msg).join(', ');
      }
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const shareOnWhatsApp = () => {
    if (!credentialsModal) return;
    const message = `Welcome to CloudCampus ERP!\n\nYour Staff Portal Login Credentials:\n\nEmployee ID: ${credentialsModal.employeeId}\nUsername: ${credentialsModal.username}\nPassword: ${credentialsModal.password}\n\nLogin at: ${window.location.origin}/staff-portal\n\nPlease change your password on first login.`;
    const whatsappNo = credentialsModal.whatsapp_no?.replace(/\D/g, '') || '';
    const url = whatsappNo 
      ? `https://wa.me/91${whatsappNo}?text=${encodeURIComponent(message)}`
      : `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="outline" onClick={() => navigate('/hr/staff-list')} className="flex items-center gap-2">
          <ArrowLeft size={20} /> Back
        </Button>
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Add New Staff Member</h1>
          <p className="text-gray-600 mt-1">Complete staff registration form</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-6">
        
        {/* Basic Information */}
        <div className="border-b pb-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Basic Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">First Name <span className="text-red-500">*</span></label>
              <input name="first_name" value={formData.first_name} onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg ${errors.first_name ? 'border-red-500' : 'border-gray-300'}`}
                placeholder="Enter first name" />
              {errors.first_name && <p className="text-red-500 text-xs mt-1">{errors.first_name}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Middle Name</label>
              <input name="middle_name" value={formData.middle_name} onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="Enter middle name" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Last Name <span className="text-red-500">*</span></label>
              <input name="last_name" value={formData.last_name} onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg ${errors.last_name ? 'border-red-500' : 'border-gray-300'}`}
                placeholder="Enter last name" />
              {errors.last_name && <p className="text-red-500 text-xs mt-1">{errors.last_name}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Father Name</label>
              <input name="father_name" value={formData.father_name} onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="Enter father's name" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Mother Name</label>
              <input name="mother_name" value={formData.mother_name} onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="Enter mother's name" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Marital Status</label>
              <select name="marital_status" value={formData.marital_status} onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                <option value="Single">Single</option>
                <option value="Married">Married</option>
                <option value="Divorced">Divorced</option>
                <option value="Widowed">Widowed</option>
              </select>
            </div>

            {formData.marital_status === 'Married' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Spouse Name</label>
                <input name="spouse_name" value={formData.spouse_name} onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="Enter spouse name" />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth <span className="text-red-500">*</span></label>
              <input type="date" name="date_of_birth" value={formData.date_of_birth} onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg ${errors.date_of_birth ? 'border-red-500' : 'border-gray-300'}`} />
              {errors.date_of_birth && <p className="text-red-500 text-xs mt-1">{errors.date_of_birth}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
              <select name="gender" value={formData.gender} onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Blood Group</label>
              <select name="blood_group" value={formData.blood_group} onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                <option value="">Select</option>
                <option value="A+">A+</option>
                <option value="A-">A-</option>
                <option value="B+">B+</option>
                <option value="B-">B-</option>
                <option value="O+">O+</option>
                <option value="O-">O-</option>
                <option value="AB+">AB+</option>
                <option value="AB-">AB-</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Aadhaar Number</label>
              <input name="aadhaar_no" value={formData.aadhaar_no} onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg" 
                placeholder="12-digit Aadhaar number" maxLength={12} />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">PAN Number</label>
              <input name="pan_no" value={formData.pan_no} onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg uppercase" 
                placeholder="e.g., ABCDE1234F" maxLength={10} />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Personal Email</label>
              <input type="email" name="personal_email" value={formData.personal_email} onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="personal@email.com" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone No. <span className="text-red-500">*</span></label>
              <input name="phone" value={formData.phone} onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg ${errors.phone ? 'border-red-500' : 'border-gray-300'}`}
                placeholder="10-digit mobile number" maxLength={10} />
              {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Alternate Phone</label>
              <input name="alternate_phone" value={formData.alternate_phone} onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="Emergency contact" maxLength={10} />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">WhatsApp No.</label>
              <input name="whatsapp_no" value={formData.whatsapp_no} onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="WhatsApp number" maxLength={10} />
            </div>
          </div>
        </div>

        {/* Address Section */}
        <div className="border-b pb-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Address</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">Street Address</label>
              <textarea name="address" value={formData.address} onChange={handleChange} rows="2"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                placeholder="House No, Street, Locality"></textarea>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">State <span className="text-red-500">*</span></label>
              <select name="state" value={formData.state} onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg ${errors.state ? 'border-red-500' : 'border-gray-300'}`}>
                <option value="">Select State</option>
                {Object.keys(indianStates).sort().map(state => (
                  <option key={state} value={state}>{state}</option>
                ))}
              </select>
              {errors.state && <p className="text-red-500 text-xs mt-1">{errors.state}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">District <span className="text-red-500">*</span></label>
              <select name="district" value={formData.district} onChange={handleChange} disabled={!formData.state}
                className={`w-full px-3 py-2 border rounded-lg ${errors.district ? 'border-red-500' : 'border-gray-300'} ${!formData.state ? 'bg-gray-100' : ''}`}>
                <option value="">{formData.state ? 'Select District' : 'Select State First'}</option>
                {availableDistricts.map(district => (
                  <option key={district} value={district}>{district}</option>
                ))}
              </select>
              {errors.district && <p className="text-red-500 text-xs mt-1">{errors.district}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">PIN Code <span className="text-red-500">*</span></label>
              <input name="pin_code" value={formData.pin_code} onChange={handleChange} maxLength="6"
                className={`w-full px-3 py-2 border rounded-lg ${errors.pin_code ? 'border-red-500' : 'border-gray-300'}`}
                placeholder="6-digit PIN code" />
              {errors.pin_code && <p className="text-red-500 text-xs mt-1">{errors.pin_code}</p>}
            </div>
          </div>
        </div>

        {/* Employment Information */}
        <div className="border-b pb-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Employment Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Employee ID</label>
              <input name="employee_id" value={formData.employee_id} readOnly
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 font-medium" />
              <p className="text-xs text-gray-500 mt-1">Auto-generated</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Staff Type <span className="text-red-500">*</span></label>
              <input 
                type="text"
                name="staff_type" 
                value={formData.staff_type} 
                readOnly
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed"
              />
              <p className="text-xs text-gray-500 mt-1">Auto-set from Designation</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contract Type</label>
              <select name="contract_type" value={formData.contract_type} onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                <option value="Permanent">Permanent</option>
                <option value="Contract">Contract</option>
                <option value="Temporary">Temporary</option>
                <option value="Internship">Internship</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Department <span className="text-red-500">*</span></label>
              <select name="department" value={formData.department} onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg ${errors.department ? 'border-red-500' : 'border-gray-300'}`}>
                <option value="">Select Department</option>
                {departments.map(dept => (
                  <option key={dept.id} value={dept.name}>{dept.name}</option>
                ))}
              </select>
              {errors.department && <p className="text-red-500 text-xs mt-1">{errors.department}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Designation <span className="text-red-500">*</span></label>
              <select name="designation" value={formData.designation} onChange={handleChange} disabled={!formData.department}
                className={`w-full px-3 py-2 border rounded-lg ${errors.designation ? 'border-red-500' : 'border-gray-300'} ${!formData.department ? 'bg-gray-100' : ''}`}>
                <option value="">{!formData.department ? 'Select Department First' : 'Select Designation'}</option>
                {designations
                  .filter(desig => {
                    if (!formData.department) return false;
                    const selectedDept = departments.find(d => d.name === formData.department);
                    return selectedDept && desig.department_id === selectedDept.id;
                  })
                  .map(desig => (
                    <option key={desig.id} value={desig.title || desig.name}>{desig.title || desig.name}</option>
                  ))
                }
              </select>
              {errors.designation && <p className="text-red-500 text-xs mt-1">{errors.designation}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Qualification</label>
              <input name="qualification" value={formData.qualification} onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="e.g., M.Sc, PhD" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Highest Qualification (NAAC)</label>
              <select name="highest_qualification" value={formData.highest_qualification} onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                <option value="">Select Highest Qualification</option>
                <option value="Ph.D">Ph.D / Doctorate</option>
                <option value="M.Phil">M.Phil</option>
                <option value="Post Graduate">Post Graduate (M.A/M.Sc/M.Com/MBA/M.Tech)</option>
                <option value="Graduate">Graduate (B.A/B.Sc/B.Com/B.Tech)</option>
                <option value="Diploma">Diploma</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Joining Date <span className="text-red-500">*</span></label>
              <input type="date" name="joining_date" value={formData.joining_date} onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg ${errors.joining_date ? 'border-red-500' : 'border-gray-300'}`} />
              {errors.joining_date && <p className="text-red-500 text-xs mt-1">{errors.joining_date}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ending Date</label>
              <input type="date" name="ending_date" value={formData.ending_date} onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Monthly Salary</label>
              <input type="number" name="salary" value={formData.salary} onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="Enter monthly salary" />
            </div>
          </div>
        </div>

        {/* Bank Details */}
        <div className="border-b pb-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Bank Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Account Holder Name</label>
              <input name="account_holder_name" value={formData.account_holder_name} onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="As per bank records" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Account Type</label>
              <select name="account_type" value={formData.account_type} onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                <option value="">Select Type</option>
                <option value="Savings">Savings Account</option>
                <option value="Current">Current Account</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Bank Name</label>
              <input name="bank_name" value={formData.bank_name} onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="e.g., State Bank of India" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Account Number</label>
              <input name="account_number" value={formData.account_number} onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="Enter account number" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Account Number</label>
              <input name="confirm_account_number" value={formData.confirm_account_number} onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="Re-enter account number" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">IFSC Code</label>
              <input name="ifsc_code" value={formData.ifsc_code} onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg uppercase" placeholder="e.g., SBIN0001234" maxLength={11} />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Branch Name</label>
              <input name="branch_name" value={formData.branch_name} onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="e.g., Park Street Branch" />
            </div>
          </div>
        </div>

        {/* Login Credentials */}
        <div className="border-b pb-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Login Credentials</h2>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-blue-800">
              <strong>Username:</strong> <code className="bg-white px-2 py-1 rounded">{formData.employee_id || 'Auto-generated'}</code> (Employee ID will be the username)
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password (Optional)</label>
              <div className="relative">
                <input name="password" type={showPassword ? "text" : "password"} value={formData.password} onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg pr-10" placeholder="Leave blank to auto-generate" minLength={6} />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">Leave blank to auto-generate. Minimum 6 characters for manual entry.</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Account Status</label>
              <select name="account_status" value={formData.account_status} onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
          </div>
        </div>

        {/* System Roles */}
        <div className="border-b pb-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">System Roles</h2>
          <p className="text-sm text-gray-600 mb-4">Select the roles for this staff member. Multiple roles can be assigned.</p>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {availableRoles.map(role => (
              <div key={role.key} onClick={() => toggleRole(role.key)}
                className={`p-3 border-2 rounded-lg cursor-pointer transition-all ${
                  formData.roles.includes(role.key) ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                }`}>
                <div className="flex items-center gap-2">
                  <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                    formData.roles.includes(role.key) ? 'bg-blue-500 border-blue-500' : 'border-gray-300'
                  }`}>
                    {formData.roles.includes(role.key) && <CheckCircle size={12} className="text-white" />}
                  </div>
                  <span className="font-medium text-sm">{role.name}</span>
                </div>
                <p className="text-xs text-gray-500 mt-1 ml-6">{role.description}</p>
              </div>
            ))}
          </div>
          
          {formData.roles.length === 0 && (
            <p className="text-amber-600 text-sm mt-3">⚠️ Please select at least one role</p>
          )}
        </div>

        {/* Submit Button */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button type="button" variant="outline" onClick={() => navigate('/hr/staff-list')}>Cancel</Button>
          <Button type="submit" disabled={loading} className="bg-blue-600 hover:bg-blue-700">
            {loading ? 'Saving...' : <><Save size={18} className="mr-2" /> Save Staff</>}
          </Button>
        </div>
      </form>

      {/* Credentials Modal */}
      {credentialsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Staff Created Successfully!</h3>
              <button onClick={() => { setCredentialsModal(null); navigate('/hr/staff-list'); }}
                className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
              <p className="text-green-800 font-medium mb-2">{credentialsModal.staffName}</p>
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Employee ID:</span>
                  <div className="flex items-center gap-2">
                    <code className="bg-white px-2 py-1 rounded">{credentialsModal.employeeId}</code>
                    <button onClick={() => copyToClipboard(credentialsModal.employeeId)} className="text-gray-400 hover:text-gray-600">
                      <Copy size={14} />
                    </button>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Username:</span>
                  <div className="flex items-center gap-2">
                    <code className="bg-white px-2 py-1 rounded">{credentialsModal.username}</code>
                    <button onClick={() => copyToClipboard(credentialsModal.username)} className="text-gray-400 hover:text-gray-600">
                      <Copy size={14} />
                    </button>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Password:</span>
                  <div className="flex items-center gap-2">
                    <code className="bg-white px-2 py-1 rounded">{credentialsModal.password}</code>
                    <button onClick={() => copyToClipboard(credentialsModal.password)} className="text-gray-400 hover:text-gray-600">
                      <Copy size={14} />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={shareOnWhatsApp}>
                <MessageCircle size={16} className="mr-2" /> Share via WhatsApp
              </Button>
              <Button className="flex-1 bg-blue-600 hover:bg-blue-700" onClick={() => { setCredentialsModal(null); navigate('/hr/staff-list'); }}>
                Done
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddStaffPage;
